// useAvailableScreenHeight.js
import { useState, useEffect } from 'react';
import useMeasure from 'react-use-measure';
import useGetScreenSize from './useGetScreenSize'; // Adjust the import path as needed

/**
 * Custom hook to calculate the available height for a component based on a condition.
 *
 * @param {number} bottomPadding - Optional bottom padding to subtract from the available height.
 * @param {boolean} condition - Determines when the hook should actively calculate and apply the height.
 * @returns An array containing a ref to be attached to the component and the calculated or default height.
 */
const useAvailableScreenHeight = (condition, bottomPadding = 0) => {
	const screenHeight = useGetScreenSize();
	const [componentRef, bounds] = useMeasure();
	const [availableHeight, setAvailableHeight] = useState('auto');

	useEffect(() => {
		if (condition) {
			// Only calculate and apply the height if the condition is met
			const totalAvailableHeight = screenHeight - bounds.top - bottomPadding;
			setAvailableHeight(`${totalAvailableHeight}px`);
		} else {
			// If the condition is not met, revert to default
			setAvailableHeight('auto');
		}
	}, [bounds.top, screenHeight, bottomPadding, condition]);

	return [componentRef, availableHeight];
};

export default useAvailableScreenHeight;
